/**
 * "Practice" (spec 53 8.1), in the practice fleet only: stage the module's
 * starting state on the practice tenant (5.4), then start it on its first
 * route. By default that is the task and its checklist (`?task=<id>`,
 * TaskHost, spec 53 15); `guided` starts the card-by-card walkthrough
 * instead (`?tour=<id>`, TourHost; mayStart lets a training definition run
 * on a training lease).
 *
 * Watching the video first isn't required (14.9).
 */
import { loadTour, TOUR_IDS } from '../tours/registry';

/**
 * The ui definition that teaches a server module, or null while none is
 * written (WP5 to WP7 write the twenty). Usually the tour id is the module
 * id (4.2's example); otherwise the registered definitions are asked.
 */
export async function definitionFor(moduleId) {
  const teaches = definition =>
    Boolean(definition && definition.training && definition.training.module === moduleId);
  if (TOUR_IDS.includes(moduleId)) {
    const definition = await loadTour(moduleId);
    if (teaches(definition)) return definition;
  }
  for (const id of TOUR_IDS) {
    if (id === moduleId || id === 'sales') continue;
    const definition = await loadTour(id);
    if (teaches(definition)) return definition;
  }
  return null;
}

/**
 * Where the walkthrough opens: the first route a step names that needs no
 * lookup in the fleet (a `{ load: ... }` param is TourHost's to resolve once
 * the run is going), or the board.
 */
export function firstRoute(definition) {
  const steps = ((definition && definition.chapters) || []).flatMap(c => c.steps || []);
  for (const step of steps) {
    const route = step && step.route;
    if (!route) continue;
    const params = route.params || {};
    if (Object.values(params).some(v => v && typeof v === 'object')) break;
    return route;
  }
  return { name: 'Dispatch' };
}

/**
 * Stage and start. Answers { ok: true } once the walkthrough's address is
 * pushed, or { ok: false, reason } with reason 'not-ready' (no definition
 * yet) or 'stage' (the practice fleet could not build the starting state).
 */
export async function practise({ http, router, moduleId, guided = false }) {
  const definition = await definitionFor(moduleId);
  if (!definition) return { ok: false, reason: 'not-ready' };
  const stage = definition.training && definition.training.stage;
  if (stage) {
    try {
      await http.post('/demo/training/stage', { stage, module: moduleId });
    } catch (err) {
      return { ok: false, reason: 'stage' };
    }
  }
  const route = firstRoute(definition);
  await router
    .push({
      ...route,
      query: { ...(route.query || {}), [guided ? 'tour' : 'task']: definition.id },
    })
    .catch(() => {});
  return { ok: true };
}
